import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  adjustFromTimeZone,
  adjustToTimeZone,
  getObjectId,
  LocationStatusFacet,
  locationStatusFacetSelector,
  useGetLocationStatusFacetQuery
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {
  LocationStatusFacetForm,
  LocationStatusFacetFormValues
} from "../../components/form/LocationStatusFacetForm/LocationStatusFacetForm";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {useLocationStatusFacetMutation} from "../../hooks/useLocationStatusFacetMutation";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ErrorPage} from "../../components/page/ErrorPage/ErrorPage";
import {useAuth} from "../../providers/AuthProvider/AuthProvider";
import {
  toFormDefaultsFromLocationStatusFacet
} from "../../components/form/LocationStatusFacetForm/useLocationStatusFacetForm";

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: Partial<LocationStatusFacetFormValues>
}

export const LocationStatusFacetFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  return (
    <LocationStatusFacetFormPage
      id={id}
      copy={copy}
      formDefaults={formDefaults}
    />
  );
};

export const LocationStatusFacetFormPage = (props: Props) => {
  const {
    id,
    copy = false,
    formDefaults,
  } = props;

  const {regionId} = useApp()
  const {hasAccess} = useAuth()

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  const selectLocationStatusFacet = useMemo(() => {
    return locationStatusFacetSelector()
  }, [])

  const {currLocationStatusFacet, isLoading} = useGetLocationStatusFacetQuery(id ?? skipToken, {
    selectFromResult: (result) => ({
      currLocationStatusFacet: selectLocationStatusFacet(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: Partial<LocationStatusFacetFormValues> = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaultsFromLocationStatusFacet(currLocationStatusFacet);
  }, [currLocationStatusFacet, formDefaults]);

  const title: string = useMemo(() => {
    if (currLocationStatusFacet && !copy) {
      return t("page.entity.locationstatusfacet.form.title.edit", "Edit Location Status", {ns: "common"});
    } else {
      return t("page.entity.locationstatusfacet.form.title.new", "New Location Status", {ns: "common"});
    }
  }, [currLocationStatusFacet, copy, t]);

  const {
    createLocationStatusFacet,
    updateLocationStatusFacet,
    isCreating,
    isUpdating,
  } = useLocationStatusFacetMutation()

  const onLocationStatusFacetSubmit = useCallback((formValues: LocationStatusFacetFormValues) => {
    const patch: Partial<LocationStatusFacet> = {
      siteId: getObjectId(formValues.site),
      serviceTypeId: getObjectId(formValues.serviceType),
      statusFacet: formValues.statusFacet,
      status: formValues.status,
      asOfDttm: adjustFromTimeZone(formValues.asOfDate).getTime(),
      serviceMode: formValues.serviceMode,
      confidencePct: formValues.confidencePct,
      note: formValues.note,
    }

    if (currLocationStatusFacet && !copy) {
      const loc: Partial<LocationStatusFacet> = {
        ...currLocationStatusFacet.asTypeValue(),
        ...patch,
        lastModifiedDttm: adjustToTimeZone().getTime(),
      };
      updateLocationStatusFacet(loc, () => navigate(-1))
    } else {
      const loc: Partial<LocationStatusFacet> = {
        ...patch,
        source: "MANUAL",
      };
      createLocationStatusFacet(loc, () => navigate(-1))
    }
  }, [currLocationStatusFacet, copy, createLocationStatusFacet, updateLocationStatusFacet, navigate]);

  return (
    <ThemedPage>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            {isLoading && <Loader/>}
            {!isLoading && (
              <Paper miw={500} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {title}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                <LocationStatusFacetForm
                  options={{
                    regionId: regionId,
                    siteSelectDisabled: typeof currLocationStatusFacet !== 'undefined' || typeof formDefaults?.site !== 'undefined'
                  }}
                  defaultValues={_formDefaults}
                  submitting={isCreating || isUpdating}
                  onSubmit={onLocationStatusFacetSubmit}/>
              </Paper>
            )}
          </Center>
        </Container>
      )}
    </ThemedPage>
  );
};

